import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { theme } from './theme';

function PriceEstimateCard({ parts, jobs, maintenance }) {
  const { t } = useTranslation();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(price);
  };

  // Calculate totals — public view shows only customer-facing gross prices
  const partsTotal = parts?.reduce((sum, p) => sum + parseFloat(p.line_total_incl_vat || p.line_total || 0), 0) || 0;
  const laborTotal = jobs?.reduce((sum, j) => sum + parseFloat(j.line_total_incl_vat || j.line_total || 0), 0) || 0;
  const subtotal = partsTotal + laborTotal;

  const orderDiscount = parseFloat(maintenance?.order_discount_total || 0);
  const hasOrderDiscount = orderDiscount > 0;
  const total = hasOrderDiscount ? parseFloat(maintenance.total_with_vat ?? subtotal) : subtotal;

  if (partsTotal === 0 && laborTotal === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: theme.colors.primary,
        borderRadius: theme.radius.xl,
        padding: '20px',
        color: 'white',
        marginBottom: '20px',
      }}
    >
      <Typography
        component="h3"
        sx={{ fontSize: '14px', fontWeight: '600', margin: '0 0 16px 0', opacity: 0.8 }}
      >
        {t('public_maintenance.price_estimate')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.8 }}>
          <span>{t('public_maintenance.parts_total')}</span>
          <span>{formatPrice(partsTotal)}</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.8 }}>
          <span>{t('public_maintenance.labor')}</span>
          <span>{formatPrice(laborTotal)}</span>
        </Box>
        {hasOrderDiscount && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.8 }}>
              <span>{t('public_maintenance.subtotal')}</span>
              <span>{formatPrice(subtotal)}</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.8, color: '#a5d6a7' }}>
              <span>{t('public_maintenance.discount')}</span>
              <span>{`\u2212${formatPrice(orderDiscount)}`}</span>
            </Box>
          </>
        )}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '700' }}>
          <span>{t('public_maintenance.total_with_vat')}</span>
          <span>{formatPrice(total)}</span>
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: '16px',
          padding: '10px 14px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: theme.radius.md,
          fontSize: '12px',
          opacity: 0.8,
        }}
      >
        {t('public_maintenance.price_disclaimer')}
      </Box>
    </Paper>
  );
}

export default PriceEstimateCard;
